import React from 'react';
import { withFormsy } from 'formsy-react';
import { type TextFieldProps } from '@mui/material';

import BaseTextField, { type FieldProps } from './components/BaseTextField';

type SimpleTextFieldProps = FieldProps<string> &
	Pick<TextFieldProps, 'type' | 'placeholder' | 'multiline' | 'autoComplete'>;

/** An input component used to display and edit text-based values */
function SimpleTextField({ name, type = 'text', sendChange, ...props }: SimpleTextFieldProps) {
	const ref = React.useRef<HTMLInputElement>(null);

	const cancelAutocomplete = React.useCallback(
		(element: HTMLInputElement) => {
			if (!element.hasAttribute('autocompleted')) return;
			element.removeAttribute('autocompleted');
			sendChange(name, element.value);
		},
		[name, sendChange]
	);

	const onAutocomplete = React.useCallback(
		(element: HTMLInputElement) => {
			if (element.hasAttribute('autocompleted')) return;
			element.setAttribute('autocompleted', '');
			sendChange(name, element.value);
		},
		[name, sendChange]
	);

	/**
	 * Handler for -webkit based browser that listen for mui-auto-fill-* animations.
	 * Works with Chrome, Opera, Safari and Edge (Chromium based)
	 */
	const onAnimationStart = React.useCallback(
		(event: AnimationEvent) => {
			if (event.target !== null) {
				if (event.animationName === 'mui-auto-fill-start') onAutocomplete(event.target as HTMLInputElement);
				else cancelAutocomplete(event.target as HTMLInputElement);
			}
		},
		[cancelAutocomplete, onAutocomplete]
	);

	/**
	 * Handler for non-webkit based browser that listen for input
	 * event to trigger the autocomplete-cancel process.
	 * Works with Firefox, Edge (Old version), IE11
	 */
	const onInput = React.useCallback(
		/** @param {HTMLInputElement} event */
		event => {
			if (event.inputType === 'insertReplacementText' || !('data' in event)) onAutocomplete(event.target);
			else cancelAutocomplete(event.target);
		},
		[cancelAutocomplete, onAutocomplete]
	);

	// NOTE: before and after change are not triggered if the field is autocompleted by the browser
	const handleChange = newValue => {
		if (props.beforeChange) props.beforeChange(newValue);
		sendChange(name, newValue);
		if (props.afterChange) props.afterChange(newValue);
	};

	function handleInputChange(event) {
		props.setValue(event.target.value);
	}

	const handleInputBlur = event => {
		if (event.target.hasAttribute('autocompleted')) return;
		handleChange(event.target.value);
	};

	const HelperText = ({ children }) => (
		<span className="flex justify-between">
			<span>{children}</span>
			{props.validations?.maxLength && (
				<span className="text-right">
					{props.value?.length || 0}/{props.validations.maxLength}
				</span>
			)}
		</span>
	);

	React.useEffect(() => {
		if (!ref.current) return undefined;

		const element = ref.current;
		element.addEventListener('animationstart', onAnimationStart, true);
		element.addEventListener('input', onInput, true);

		return () => {
			element.removeEventListener('animationstart', onAnimationStart, true);
			element.removeEventListener('input', onInput, true);
		};
	}, [onAnimationStart, onInput]);

	return (
		<BaseTextField
			inputRef={ref}
			className={props.className}
			name={name}
			label={props.label}
			type={type}
			value={props.value || ''}
			placeholder={props.placeholder}
			multiline={props.multiline}
			autoComplete={props.autoComplete}
			isEdit={props.isEdit}
			startAdornment={props.startAdornment}
			endAdornment={props.endAdornment}
			required={props.required}
			helperText={
				props.isEdit && (props.helperText || props.validations?.maxLength) ? (
					<HelperText>{props.helperText}</HelperText>
				) : undefined
			}
			errorMessage={props.errorMessage ? <HelperText>{props.errorMessage}</HelperText> : undefined}
			isFormSubmitted={props.isFormSubmitted}
			isPristine={props.isPristine}
			isValid={props.isValid}
			logProps={props.logProps}
			onChange={handleInputChange}
			onBlur={handleInputBlur}
		/>
	);
}

export default React.memo(withFormsy(SimpleTextField));
